import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ReloadIcon } from "@radix-ui/react-icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { Card } from "@/components/ui/card";
import { resetPassword } from "aws-amplify/auth";
import AdminVerificationCode from "./AdminVerificationCode";

const schema = yup
  .object({
    email: yup
      .string()
      .email("Invalid email address")
      .required("Email address is required."),
  })
  .required();

const AdminForgotPassword = () => {
  const [showVerification, setShowVerification] = React.useState(false);
  const {
    register,
    setError,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  async function handleResetPassword({ email }) {
    try {
      // Directly proceed with password reset - AWS Cognito will handle user validation
      const output = await resetPassword({ username: email.toLowerCase() });
      
      if (output.nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
        setShowVerification(true);
      }
    } catch (err) {
      // Handle specific AWS Cognito errors
      let errorMessage = "Failed to send reset email. Please try again.";
      
      if (err.name === 'UserNotFoundException') {
        errorMessage = "No account found with this email address.";
      } else if (err.name === 'InvalidParameterException') {
        errorMessage = "Invalid email address format.";
      } else if (err.name === 'LimitExceededException') {
        errorMessage = "Too many requests. Please try again later.";
      }
      
      setError("root", {
        message: errorMessage,
      });
      console.log("Admin password reset error:", err);
    }
  }

  const onSubmit = async (data) => {
    await handleResetPassword(data);
  };

  return (
    <div>
      {showVerification ? (
        <AdminVerificationCode UserEmail={getValues("email")} />
      ) : (
        <div className="relative min-h-screen bg-zinc-900">
          <div
            className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
            style={{ backgroundImage: `url(/bg-new.jpg)` }}
          />

          <div className="flex flex-col h-full items-center justify-center relative z-10">
            <div className="mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12">
              <img
                alt="logo"
                src="/TriVista_Horizontal_Logo_White.png"
                className="w-[300px]"
              />
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="max-w-2xl flex flex-col items-center justify-center mx-8">
                <h1 className="text-3xl text-white text-center lg:tracking-[0.75rem] tracking-[0.5rem]">
                  Admin Portal
                </h1>
                <p className="mt-6 text-white text-lg text-center">
                  Recover your admin account by providing your email address.
                </p>
                
                <Card className="mt-4 w-full mx-8 md:mx-auto md:w-auto px-4">
                  {errors.root && (
                    <Alert variant="destructive" className="mt-4 max-w-96">
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      <AlertTitle>Error</AlertTitle>
                      <AlertDescription>{errors.root.message}</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="my-4">
                    <Label className="text-xl">Email</Label>
                    <Input
                      className="h-8 w-80 sm:w-auto sm:min-w-80"
                      {...register("email")}
                    />
                    <div>
                      {errors.email && (
                        <p className="text-red-500 mt-1">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    className="my-4 w-full px-12 py-6 text-xl rounded-xl"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting && (
                      <ReloadIcon className="w-6 h-6 animate-spin mr-2" />
                    )}
                    {isSubmitting ? "Sending Reset Email..." : "Send Reset Email"}
                  </Button>
                </Card>
              </div>
            </form>
            
            <Link
              to={"/admin/login"}
              className="text-white mt-4 flex items-center gap-2"
            >
              <ArrowLeft className="h-3 w-3" /> Back to Admin Login
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminForgotPassword;